import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@patient-watch/ui";
import { formComponents } from "@patient-watch/forms";
import { formSearchParamsSchema } from "../../types/formSearchParams";
import FormStepWrapper from "../../components/FormStepWrapper";
import FormButtons from "../../components/FormButtons";
import { useFormData } from "../../hooks/useFormData";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import FormLoading from "../../components/FormLoading";
import { useCopy } from "../../i18n/locale";

const formSchema = (message: string) =>
    z.object({
        notes: z.string().max(100, message).optional(),
    });

type FormData = z.infer<ReturnType<typeof formSchema>>;

export const Route = createFileRoute("/form/notes")({
    component: NotesComponent,
    validateSearch: (search) => formSearchParamsSchema.parse(search),
});

function NotesComponent() {
    const { entry_id, profile_id } = useSearch({ from: "/form/notes" });
    const navigate = useNavigate();
    const copy = useCopy();

    const form = useForm<FormData>({
        resolver: zodResolver(formSchema(copy.notes.tooLong)),
    });

    const { isLoading } = useFormData({
        entry_id,
        form,
        fields: ["notes"],
    });

    const { submitting, submitForm } = useFormSubmission({
        entry_id,
        profile_id,
        onSuccess: () => {
            if (profile_id) {
                navigate({
                    to: "/form-complete",
                    search: { profile_id },
                });
            } else {
                navigate({
                    to: "/form-complete",
                });
            }
        },
    });

    const onSubmit = async (data: FormData) => {
        await submitForm({
            notes: data.notes || null,
            submitted_at: new Date().toISOString(),
        });
    };

    const onPrevious = () => {
        navigate({
            to: "/form/glucose-issue",
            search: { entry_id, profile_id } as any,
        });
    };

    if (isLoading) {
        return (
            <FormStepWrapper currentStep={4}>
                <FormLoading />
            </FormStepWrapper>
        );
    }

    return (
        <FormStepWrapper currentStep={5}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 w-full max-w-md">
                    <h2 className="block font-semibold text-xl text-center">{copy.notes.question}</h2>
                    <p className="text-gray-600 text-sm text-center">{copy.notes.hint}</p>

                    <formComponents.Textarea
                        name="notes"
                        label=""
                        placeholder={copy.notes.placeholder}
                        description={copy.notes.max}
                    />
                    <p className="text-gray-500 text-sm text-center">
                        {copy.notes.count(form.watch("notes")?.length || 0)}
                    </p>

                    {form.formState.errors.notes && (
                        <div className="bg-red-50 mt-4 p-3 border border-red-200 rounded-lg">
                            <p className="text-red-800 text-sm">{form.formState.errors.notes.message}</p>
                        </div>
                    )}

                    <FormButtons
                        submitting={submitting}
                        showPrevious
                        onPrevious={onPrevious}
                        submitLabel={copy.common.submit}
                    />
                </form>
            </Form>
        </FormStepWrapper>
    );
}
